import './container-page.scss'

import * as _ from 'lodash'
import { identity } from 'lodash'
import { Observable, of } from 'rxjs'
import {
    delay,
    map,
    retryWhen,
    switchMap,
    take,
    tap,
} from 'rxjs/operators'
import Component from 'vue-class-component'
import {
    faExclamationTriangle,
    faCheck,
    faTimes,
    faCircleNotch,
} from '@fortawesome/free-solid-svg-icons'

import { Container, Item, ItemTypeWrapper, SearchResult, Wemi } from '@skinsoft/types'
import { API_ENDPOINTS } from '@skinsoft/network'
import { ContainerDataStore, ItemDataStore } from '@skinsoft/entity'
import { ReactiveVue } from '@skinsoft/reactive-vue'
import { ContainerFeaturedCard } from '@skinsoft/entity-ui/src/container'
import { AddAccessoryModal, AddContainerModal, ItemCard, Touchable } from '@skinsoft/ui'
import { SelectItemMultipleModal } from '../../component/select-item-multiple-modal'
import { ContainerItemList } from './container-item-list'
import {
    ADD_ACCESSORY_TOPIC,
    ADD_CONTAINER_TOPIC,
    ADD_ITEM_TOPIC,
    ADD_LOCATION_TOPIC,
    CLOSE_CONTAINER_TOPIC,
    DELETE_CONTAINER_TOPIC,
    OPEN_CONTAINER_TOPIC,
} from '../../../busTopics'
import { getContentTypeFromTypeKey, ListContentType } from './list-content-type'
import { ChooseItemModal } from '../../component/choose-item-modal'
import { Routes } from '../../../router'
import { DELETE_CARD } from '@skinsoft/events'
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus/src/busTopics'
import { searchResultSelectors } from '@skinsoft/search-data-store'

declare var ITEM_SEARCH_TYPES: string[]

// we define the interface here and not in type as it is only meant to be used inside this file
interface ExpectedChange {
    type: 'update' | 'delete' | 'create'
    expectedValue?: {
        // we expect item with id idItem to have a property corresponding to the path with the adequate value
        // if it is the case, we know the update was successful
        idItem: string
        path: string
        value: any
    }
}

@Component({
    components: {
        sItemCard: ItemCard,
        sContainerFeaturedCard: ContainerFeaturedCard,
        sSelectItemMultipleModal: SelectItemMultipleModal,
        sTouchable: Touchable,
        sAddAccessoryModal: AddAccessoryModal,
        sAddContainerModal: AddContainerModal,
        sContainerItemList: ContainerItemList,
        sChooseItemModal: ChooseItemModal
    },
    template: require('./container-page.html'),
})
export class ContainerPage extends ReactiveVue {
    public $containerDataStore: ContainerDataStore
    public $itemDataStore: ItemDataStore
    public exclamationTriangleIcon = faExclamationTriangle
    public checkIcon = faCheck
    public crossIcon = faTimes
    public spinnerIcon = faCircleNotch
    public $refs: {
        addItemModal: SelectItemMultipleModal
        addAccessoryModal: SelectItemMultipleModal
        addContainerModal: SelectItemMultipleModal
        chooseItemModal: ChooseItemModal
    }

    ListContentType = ListContentType
    public itemTypes = ITEM_SEARCH_TYPES
    public containerTypes = ['physicalContainerItem', 'numericContainerItem']
    public accessoryTypes = ['physicalAccessoryItem', 'numericAccessoryItem']

    private container: Container = null as any
    private items = [] as unknown as ItemTypeWrapper[]
    private subContainers = [] as ItemTypeWrapper[]
    private accessories = [] as ItemTypeWrapper[]
    private currentlyAddingTo: ListContentType | null = null

    private DELETE_CARD = DELETE_CARD

    public mounted() {
        this.subscribeToBus(ADD_ITEM_TOPIC, this.openAddItemModal)
        this.subscribeToBus(ADD_ACCESSORY_TOPIC, () => this.openAddAccessoryModal())
        this.subscribeToBus(CLOSE_CONTAINER_TOPIC, () => this.toggleContainerOpen(false))
        this.subscribeToBus(OPEN_CONTAINER_TOPIC, () => this.toggleContainerOpen())
        this.subscribeToBus(ADD_CONTAINER_TOPIC, this.openAddContainerModal)
        this.subscribeToBus(ADD_LOCATION_TOPIC, this.openLocationModal)
        this.subscribeToBus(DELETE_CONTAINER_TOPIC, this.deleteContainer)
        this.subscribeToEntity()
    }

    public subscribeToEntity(checkUpdated?: ExpectedChange) {
        this.subscribeTo(
            this.$containerDataStore.fetchOne(this.$route.params.id),
            (container) => {
                this.container = container
                this.subscribeToItems(checkUpdated)
            },
            'container',
        )
    }

    public async subscribeToItems(expectedChange?: ExpectedChange) {
        const observable$ = of(this.container.id).pipe(
            switchMap((id) =>
                this.$containerDataStore.getAssociatedItems(
                    id,
                    ITEM_SEARCH_TYPES.concat(this.containerTypes).concat(this.accessoryTypes),
                ),
            ),
            // When we fetch after a modification on the data, sometimes the result we get from 
            // this.$containerDataStore.getAssociatedItems() are not up to date yet
            tap((items) => {
                if(expectedChange) {
                    switch(expectedChange.type) {
                        case 'update':
                            if(!(expectedChange.expectedValue && expectedChange.expectedValue.idItem && expectedChange.expectedValue.path && expectedChange.expectedValue.value)) {
                                // Logger missing param
                                return // we ignore the check and continue
                            }
                            const itemToCheck = items.find(item => (item as any).id === expectedChange.expectedValue!.idItem)
        
                            if(itemToCheck) {
                                const newValue = _.get(itemToCheck, expectedChange.expectedValue.path)
                                if(newValue === expectedChange.expectedValue.value) {
                                    // OK, we have the updated version
                                    return
                                }
                            }
        
                            throw new Error('The expected changes are not available yet')
                        
                        case 'delete':
                        case 'create':
                            if(items.length === this.items.length) {
                                throw new Error('The expected changes are not available yet')
                            }
                            break
                    }
                }
            }),
            retryWhen((e) => {
                return e.pipe(
                    delay(1000),
                    take(5),
                )
            }),
            tap(items => {
                this.subContainers = items.filter((item) => {
                    const type = (item as any).entityType ?? (item as Item).type
                    return type?.includes('Container')
                })
                .map(container => {
                    return {
                        item: container,
                        wrapperType: 'SearchResult',
                        itemType: 'Container',
                    } as ItemTypeWrapper
                })
            }),
            map(items => {
                return items.filter(item => {
                    const type = (item as any).entityType ?? (item as Item).type
                    return !type?.includes('Container')
                })
            }),
            tap(items => {
                this.accessories = items.filter((item) => {
                    const type = (item as any).entityType ?? (item as Item).type
                    return type?.includes('Accessory')
                })
                .map(accessory => {
                    return {
                        item: accessory,
                        wrapperType: 'SearchResult',
                        itemType: 'Accessory',
                    } as ItemTypeWrapper
                })
            }),
            map((items) => {
                return items.filter(item => {
                    const type = (item as any).entityType ?? (item as Item).type
                    return !type?.includes('Accessory')
                })
            }),
            tap(() => this.currentlyAddingTo = null)
        )

        this.subscribeTo(
            observable$,
            (items) => {
                this.items = items.map(item => {
                    return {
                        item: item,
                        wrapperType: 'SearchResult',
                        itemType: 'Item',
                    } as ItemTypeWrapper
                })
            },
            'items',
        )
    }

    private async onAddedItemSelected({ id, type }: { id: string, type: string}) {
        this.currentlyAddingTo = getContentTypeFromTypeKey(type)
        this.$containerDataStore
            .addItem(this.container, id)
            .pipe()
            .subscribe(item => {
                if(item.type) {
                    if(item.type.includes('Container')) {
                        this.subContainers.push({
                            item: item,
                            wrapperType: 'Item',
                            itemType: 'Container',
                        })
                        this.currentlyAddingTo = null
                        return
                    }
                    else if(item.type.includes('Accessory')) {
                        this.accessories.push({
                            item: item,
                            wrapperType: 'Item',
                            itemType: 'Accessory',
                        })
                        this.currentlyAddingTo = null
                        return
                    }
                }

                this.items.push({
                    item: item,
                    wrapperType: 'Item',
                    itemType: 'Item',
                })
                this.currentlyAddingTo = null
            })
    }

    private openAddItemModal() {
        this.$refs.addItemModal.open()
    }

    private openAddAccessoryModal() {
        this.$refs.addAccessoryModal.open()
    }

    private openAddContainerModal() {
        this.$refs.addContainerModal.open()
    }

    private toggleContainerOpen(open: boolean = true) {
        if (
                (
                    this.container.containerItemSchema.openContainer 
                    && (this.container.locationItemSchema?.permanentLocation || this.container.locationItemSchema?.realTimeLocation)
                ) 
                || !this.container.containerItemSchema.openContainer
            ) {
            this.container.containerItemSchema.openContainer = open
            this.$containerDataStore.update(
                this.container.id,
                this.container,
                API_ENDPOINTS[this.container.type],
            ).subscribe(() => {
                const alertPayload = {
                    message: open ? this.$t('container.alerts.containerOpened') : this.$t('container.alerts.containerClosed'),
                    variant: "success"
                }
                this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
            })
        }
    }


    private onDelete(itemWrapper: ItemTypeWrapper) {
        // if itemWrapper.item is a SearchResult we can't use it to update (so we fetch the item first)
        let item$: Observable<Item> = this.$itemDataStore.fetchOne((itemWrapper.item as any).id)
        if(itemWrapper.wrapperType == 'Item') {
            item$ = of(itemWrapper.item as Item)
        }

        item$.pipe(
            switchMap(item => {
                return this.$containerDataStore.removeItem(this.container?.id, item as Item)
            }),
            delay(1000),
        ).subscribe(() => {
            this.unsubscribeFrom('container')
            this.unsubscribeFrom('items')
            this.subscribeToEntity({ type: 'delete' })
        })
    }

    private deleteContainer() {
        if(this.container.id) {
            this.$containerDataStore.delete(this.container.id).subscribe(() => {
                this.$router.push({ name: Routes.CONTAINERS })
            })
        }
    }

    private get itemIds() {
        return this.items.filter(identity)
            .map(wrapper => {
                if(wrapper.wrapperType === 'Wemi') {
                    return (wrapper.item as Wemi).item?.id
                }
                return (wrapper.item as SearchResult | Item ).id
            })
    }

    private get containerIds() {
        return this.subContainers.filter(identity)
            .map(wrapper => {
                if(wrapper.wrapperType === 'Wemi') {
                    return (wrapper.item as Wemi).item?.id
                }
                return (wrapper.item as SearchResult | Container ).id
            })
    }

    private get accessoryIds() {
        return this.accessories.filter(identity)
            .map(wrapper => {
                if(wrapper.wrapperType === 'Wemi') {
                    return (wrapper.item as Wemi).item?.id
                }
                return (wrapper.item as SearchResult | Item ).id
            })
    }

    private openLocationModal() {
        this.$refs.chooseItemModal.open()
    }

    private getOneExpectedChangeForLocationUpdate(locationId: string): ExpectedChange | undefined {
        const childrens = this.items.concat(this.subContainers).concat(this.accessories)

        // since the subscribeToItems method returns SearchResult or Wemi, we only return an element in this format
        // In reality it only returns SearchResult so we only implement that one yet
        // NB: We are not able to check for a change on an element that is not a SearchResult as the path of updated element might differ
        let elementThatWillBeUpdated = childrens.find(item => {
            if(item.wrapperType === 'SearchResult') {
                return searchResultSelectors.realTimeLocationId(item.item as SearchResult) !== locationId
            }

            return false
        })
        
        if(elementThatWillBeUpdated) {
            return {
                type: 'update',
                expectedValue: {
                    idItem: searchResultSelectors.id(elementThatWillBeUpdated.item as SearchResult),
                    path: searchResultSelectors.pathsStrings['realTimeLocationId'][0],
                    value: locationId,
                },
            }
        }

        return undefined
    }

    private addLocation({ location: { id: locationId }, setPermanentLocation }: { location: { id: string }, setPermanentLocation: boolean }) {
        let locationPath: string
        if (setPermanentLocation) {
            locationPath = 'locationItemSchema.permanentLocation.permanentLocation'
        } else {
            locationPath = 'locationItemSchema.realTimeLocation.realTimeLocation'
        }
        _.set(this.container, locationPath, locationId)


        const expectedChange = this.getOneExpectedChangeForLocationUpdate(locationId)

        this.$containerDataStore.update(this.container.id, this.container).pipe(
            switchMap(container => {
                return this.$containerDataStore.cascadeLocationOnContent(this.container.id)
            })
        )
        .subscribe(() => {
            this.unsubscribeFrom('container')
            this.unsubscribeFrom('items')
            if(expectedChange) {
                this.subscribeToEntity(expectedChange)
            } else {
                this.subscribeToEntity()
            }
        })
    }
}
